/**
 * Textos legales de WulfTime como proveedor del servicio (no los de cada empresa cliente con sus trabajadores).
 * Son borradores cuidados para revisión por un abogado: no prometen cumplimiento absoluto y describen lo que la
 * aplicación hace de verdad. Los datos del titular se rellenan desde la configuración del servidor.
 * Al cambiar un texto de forma sustancial, sube su `version`: las empresas tendrán que aceptarlo de nuevo.
 */

export interface LegalEntity {
  /** Nombre o razón social. */
  name: string | null;
  taxId: string | null;
  address: string | null;
  email: string | null;
  /** Datos registrales (p. ej. Registro Mercantil de …, tomo, folio, hoja). */
  registry: string | null;
  /** Proveedor de email transaccional (para la lista de subencargados). */
  emailProvider: string | null;
  /** Avisos de la app nativa por Expo (solo si está activado). */
  expoPush: boolean;
  /** Pagos con tarjeta o SEPA por Stripe (solo si está activado). */
  stripe: boolean;
  trialDays: number;
  annualDiscountPct: number;
  refreshDays: number;
}

export type LegalSlug = 'aviso-legal' | 'privacidad' | 'cookies' | 'condiciones' | 'encargo' | 'subencargados';

export interface LegalDoc {
  slug: LegalSlug;
  title: string;
  version: string;
  /** Se acepta al contratar (y de nuevo si cambia la versión). */
  requiresAcceptance: boolean;
  /** Texto en Markdown sencillo (títulos ##, listas -, negrita **). */
  body: (e: LegalEntity) => string;
}

const PENDING = (what: string) => `[pendiente: ${what}]`;
const who = (e: LegalEntity) => e.name ?? PENDING('nombre o razón social del titular');
const nif = (e: LegalEntity) => e.taxId ?? PENDING('NIF');
const addr = (e: LegalEntity) => e.address ?? PENDING('domicilio');
const mail = (e: LegalEntity) => e.email ?? PENDING('email de contacto');

export const isEntityComplete = (e: LegalEntity) => Boolean(e.name && e.taxId && e.address && e.email);

export const LEGAL_DOCS: LegalDoc[] = [
  {
    slug: 'aviso-legal',
    title: 'Aviso legal',
    version: '2026-10-18',
    requiresAcceptance: false,
    body: (e) => `
## Titular del sitio

En cumplimiento del artículo 10 de la Ley 34/2002, de servicios de la sociedad de la información y de comercio electrónico (LSSI):

- **Titular:** ${who(e)}
- **NIF:** ${nif(e)}
- **Domicilio:** ${addr(e)}
- **Email:** ${mail(e)}
${e.registry ? `- **Datos registrales:** ${e.registry}` : ''}

## Objeto

wulftime.com ofrece WulfTime, un servicio en la nube de registro de jornada y gestión de horarios para empresas (en adelante, «el servicio»). La contratación del servicio se rige por las [Condiciones del servicio](/legal/condiciones).

## Propiedad intelectual

El software, los textos, los diseños y las marcas de WulfTime pertenecen a su titular o se usan con licencia. No se permite reproducirlos ni explotarlos sin autorización, salvo el uso normal del servicio contratado.

## Responsabilidad

El titular procura que la información del sitio sea correcta y esté actualizada, pero no garantiza la ausencia de errores. WulfTime es una herramienta: ayuda a las empresas a llevar el registro de jornada, pero el cumplimiento de la normativa laboral depende también de cómo cada empresa lo configure y lo use.

## Legislación aplicable

Este aviso se rige por la legislación española.
`,
  },
  {
    slug: 'privacidad',
    title: 'Política de privacidad',
    version: '2026-10-18',
    requiresAcceptance: false,
    body: (e) => `
Esta política explica cómo trata los datos personales el titular de WulfTime como **responsable**: los de las personas que visitan la web, contratan el servicio o administran la cuenta de una empresa.

Los datos de los **trabajadores** que cada empresa registra en WulfTime (fichajes, horarios, ubicación al fichar…) los trata la empresa como responsable, y WulfTime solo como **encargado del tratamiento** por cuenta de ella, según el [Contrato de encargo](/legal/encargo). Para ejercer tus derechos sobre esos datos, dirígete a tu empresa.

## Responsable

${who(e)} · NIF ${nif(e)} · ${addr(e)} · ${mail(e)}

## Qué datos tratamos y para qué

- **Alta y uso de la cuenta** (nombre, email, empresa, contraseña cifrada, verificación en dos pasos): prestar el servicio contratado. Base jurídica: ejecución del contrato (art. 6.1.b RGPD).
- **Facturación** (razón social, NIF, dirección, importes, forma de pago): emitir facturas y cobrar. Base: obligación legal (art. 6.1.c) y contrato.
- **Seguridad** (direcciones IP, dispositivos, sesiones, registro de auditoría): proteger las cuentas y poder demostrar la integridad de los registros. Base: interés legítimo (art. 6.1.f) y obligaciones del encargo.
- **Comunicaciones del servicio** (avisos, facturas, cambios de condiciones): base contractual. No enviamos publicidad sin consentimiento.

## Cuánto tiempo

Mientras la cuenta esté activa y, después, el tiempo exigido por la ley (por ejemplo, las facturas durante los plazos de la normativa fiscal y mercantil) o necesario para atender reclamaciones.

## Con quién se comparten

Con los proveedores que ayudan a prestar el servicio, con contrato de encargo y dentro de la Unión Europea siempre que es posible: ver la [lista de subencargados](/legal/subencargados). Con las administraciones públicas cuando la ley lo exige. No vendemos datos.

## Tus derechos

Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a ${mail(e)}. Si crees que no se han atendido bien, puedes reclamar ante la Agencia Española de Protección de Datos (aepd.es).
`,
  },
  {
    slug: 'cookies',
    title: 'Política de cookies',
    version: '2026-10-18',
    requiresAcceptance: false,
    body: (e) => `
WulfTime **solo usa cookies y almacenamiento técnicos**, imprescindibles para que el servicio funcione. No usa cookies de análisis, de publicidad ni de terceros, por lo que no necesita tu consentimiento (art. 22.2 LSSI). Puedes borrarlas desde tu navegador, pero entonces tendrás que volver a iniciar sesión.

## Cookies

- **wt_refresh**: mantiene la sesión iniciada de forma segura (solo la lee el servidor). Dura hasta ${e.refreshDays} días o hasta cerrar sesión.
- **wt_td**: recuerda que este navegador es de confianza para no pedir el código de verificación en dos pasos en cada acceso, si lo marcas. Dura 30 días.

## Almacenamiento en el navegador

- Preferencias de la interfaz (por ejemplo, si has cerrado el aviso de instalar la app).
- Fichajes hechos sin conexión, hasta que se envían al servidor.
- La copia de la aplicación para abrirla sin conexión (service worker). Nunca guarda datos de la API.
`,
  },
  {
    slug: 'condiciones',
    title: 'Condiciones del servicio',
    version: '2026-10-18',
    requiresAcceptance: true,
    body: (e) => `
Estas condiciones regulan la contratación de WulfTime entre ${who(e)} (NIF ${nif(e)}, «WulfTime») y la empresa o profesional que lo contrata («el cliente»). El servicio se dirige a empresas y profesionales, no a consumidores.

## 1. El servicio

WulfTime es un servicio en la nube para registrar la jornada de los trabajadores (fichajes desde el móvil, la web o un kiosco), planificar horarios, gestionar ausencias, incidencias y correcciones, y obtener informes. Las funciones de cada plan se describen en la web.

## 2. Alta y cuentas

Quien da de alta la empresa declara tener capacidad para contratar en su nombre. El cliente es responsable de quién tiene acceso a su cuenta, de sus contraseñas y de los permisos que asigna.

## 3. Prueba gratuita

El alta incluye ${e.trialDays} días de prueba con todas las funciones del plan elegido. Si al terminar la prueba hay un método de pago, empieza el primer periodo de pago; si no, el acceso a las funciones de pago queda en pausa, sin perder los datos.

## 4. Precios, facturación y renovación

- Los precios son los publicados en la web en el momento de contratar. Cuando un plan indica «IVA incluido», el importe publicado es el total a pagar.
- El servicio se paga **por adelantado** por periodos mensuales o anuales. El anual tiene un ${e.annualDiscountPct} % de descuento. Los trabajadores que se añadan durante un periodo anual se facturan de forma proporcional.
- Los periodos se **renuevan automáticamente** hasta que el cliente cancele.
- WulfTime puede cambiar los precios avisando con al menos **30 días**; el cambio se aplica desde la siguiente renovación y el cliente puede cancelar antes.

## 5. Impago

Si una factura no se paga, WulfTime lo recordará. Mientras siga pendiente, el **registro de jornada sigue funcionando** (es una obligación legal del cliente), pero las funciones adicionales pueden quedar en pausa. Tras un impago prolongado, la suscripción puede cancelarse.

## 6. Baja

El cliente puede cancelar en cualquier momento; la baja tiene efecto al terminar el periodo ya pagado y no se devuelven periodos en curso. Durante los **30 días** siguientes podrá exportar todos sus registros. Después, los datos se suprimen, salvo lo que la ley obligue a conservar. Recuerda: la empresa debe conservar el registro de jornada **cuatro años** (art. 34.9 del Estatuto de los Trabajadores), por lo que conviene exportarlo antes.

## 7. Obligaciones del cliente

- Configurar sus políticas de jornada conforme a su convenio y a la ley, y registrar la jornada real de sus trabajadores.
- Informar a sus trabajadores del tratamiento de sus datos y, si usa la ubicación al fichar, cumplir el art. 90 de la Ley Orgánica 3/2018 (LOPDGDD).
- Usar el servicio de forma lícita y sin intentar acceder a datos de otros clientes.

## 8. Disponibilidad y soporte

WulfTime pone los medios razonables para que el servicio esté disponible y hace copias de seguridad diarias, pero no garantiza un funcionamiento ininterrumpido. Las paradas programadas se intentarán hacer fuera del horario laboral. Soporte en ${mail(e)}.

## 9. Responsabilidad

WulfTime es una herramienta: **no garantiza el cumplimiento de la normativa laboral**, que depende de cómo el cliente configure y use el servicio. La responsabilidad de WulfTime frente al cliente se limita, salvo dolo o culpa grave, al importe pagado por el cliente en los doce meses anteriores al hecho que la origine.

## 10. Protección de datos

WulfTime trata los datos de los trabajadores del cliente como encargado del tratamiento según el [Contrato de encargo](/legal/encargo), que forma parte de estas condiciones.

## 11. Cambios en las condiciones

Si estas condiciones cambian de forma relevante, se avisará al cliente y se le pedirá que las acepte en su panel.

## 12. Ley y jurisdicción

Se rigen por la ley española. Para cualquier controversia, las partes se someten a los juzgados y tribunales del domicilio de WulfTime.
`,
  },
  {
    slug: 'encargo',
    title: 'Contrato de encargo del tratamiento',
    version: '2026-10-18',
    requiresAcceptance: true,
    body: (e) => `
Contrato entre **el cliente** (responsable del tratamiento) y **${who(e)}**, NIF ${nif(e)} (WulfTime, encargado del tratamiento), según el artículo 28 del Reglamento (UE) 2016/679 (RGPD). Forma parte de las [Condiciones del servicio](/legal/condiciones).

## 1. Objeto, naturaleza y finalidad

WulfTime trata datos personales por cuenta del cliente solo para prestar el servicio: registrar la jornada, planificar horarios, gestionar ausencias, incidencias y correcciones, emitir informes y custodiar los registros.

## 2. Duración

Mientras dure el servicio y, al terminar, hasta la devolución y supresión de los datos (cláusula 9).

## 3. Datos y personas afectadas

- **Personas:** trabajadores del cliente y usuarios de su cuenta (administradores, responsables, auditores).
- **Datos:** identificativos y de contacto, centro y puesto, horarios y fichajes, ausencias e incidencias con sus justificantes, **ubicación en el momento de fichar** (solo si el cliente la activa; nunca seguimiento continuo), dispositivo, dirección IP y registro de auditoría.
- No se tratan categorías especiales de datos, salvo las que el cliente pueda incluir en justificantes de ausencias (p. ej. justificantes médicos), que se guardan con acceso restringido.

## 4. Instrucciones del cliente

WulfTime trata los datos según las instrucciones documentadas del cliente, que son su configuración del servicio (políticas, permisos, usos de la ubicación…) y este contrato. Si una instrucción pareciera contraria a la ley, WulfTime avisará al cliente.

## 5. Confidencialidad

Las personas autorizadas por WulfTime para tratar los datos están obligadas a guardar confidencialidad. El acceso de soporte a la cuenta del cliente requiere su autorización, es temporal y queda registrado.

## 6. Medidas de seguridad (art. 32 RGPD)

- Servidores en la **Unión Europea** (Alemania).
- Cifrado de las comunicaciones (HTTPS/TLS) y de los secretos guardados.
- Aislamiento de los datos de cada cliente en la propia base de datos.
- Verificación en dos pasos y control de permisos por función y ámbito.
- **Registro de auditoría encadenado y firmado**, con sello de tiempo diario de una autoridad independiente; los fichajes no se pueden modificar ni borrar sin dejar rastro.
- Copias de seguridad diarias y vigilancia del servicio.
- Ubicación solo en el momento del fichaje y solo si el cliente la activa.

## 7. Subencargados

El cliente autoriza de forma general a WulfTime a recurrir a los subencargados de la [lista publicada](/legal/subencargados). WulfTime avisará de cualquier cambio con al menos 30 días para que el cliente pueda oponerse; si se opone, podrá cancelar el servicio. WulfTime exige a cada subencargado las mismas obligaciones de protección de datos.

## 8. Asistencia al cliente

WulfTime ayudará al cliente, con las herramientas del servicio, a atender los derechos de los trabajadores (acceso, rectificación, portabilidad…), a cumplir sus obligaciones de seguridad y, si procede, en evaluaciones de impacto.

## 9. Brechas de seguridad

WulfTime notificará al cliente las brechas que afecten a sus datos **sin dilación indebida y, como máximo, en 48 horas** desde que tenga conocimiento, con la información disponible para que el cliente pueda notificarlas a la autoridad si corresponde.

## 10. Fin del servicio: devolución y supresión

Al terminar el servicio, el cliente puede exportar todos sus datos durante 30 días. Después, WulfTime los suprimirá, incluidas las copias de seguridad en su ciclo normal de rotación, salvo que la ley obligue a conservarlos.

## 11. Información y auditoría

WulfTime pondrá a disposición del cliente la información necesaria para demostrar el cumplimiento de este contrato y permitirá auditorías razonables, con preaviso y sin afectar a la seguridad de otros clientes.

## 12. Obligaciones del cliente

El cliente garantiza que tiene base legal para el tratamiento, que ha informado a sus trabajadores (en especial del uso de la ubicación, art. 90 LOPDGDD) y que configura el servicio conforme a la ley.
`,
  },
  {
    slug: 'subencargados',
    title: 'Subencargados del tratamiento',
    version: '2026-10-18',
    requiresAcceptance: false,
    body: (e) => `
Proveedores que intervienen en la prestación de WulfTime y pueden tratar datos personales de los clientes. Los cambios se anuncian con al menos 30 días de antelación.

## Proveedores

- **Hostinger International Ltd.** — Alojamiento del servidor y de la base de datos. Centro de datos en **Alemania (UE)**.
${e.emailProvider ? `- **${e.emailProvider}** — Envío de emails del servicio (invitaciones, avisos, facturas): dirección de email, nombre y contenido del aviso.` : `- **Proveedor de email** — ${PENDING('proveedor de email transaccional')}.`}
${e.stripe ? `- **Stripe Payments Europe, Ltd.** (Irlanda, UE) — Cobro con tarjeta o domiciliación SEPA: datos de facturación y de pago. WulfTime no guarda los números de tarjeta.` : ''}
${e.expoPush ? `- **Expo (650 Industries, Inc.)** (EE. UU.) — Entrega de avisos a la app nativa: identificador del dispositivo y texto del aviso. Transferencia amparada en las cláusulas contractuales tipo de la Comisión Europea o en el Marco de Privacidad de Datos UE-EE. UU.` : ''}

## Servicios que no reciben datos personales

- **Autoridades de sellado de tiempo** (DigiCert y, como respaldo, FreeTSA): reciben solo un resumen criptográfico del registro de auditoría para sellarlo, nunca datos personales.
- **Servicios de avisos de los navegadores** (Google, Apple, Mozilla): entregan los avisos web al dispositivo; el contenido viaja cifrado de extremo a extremo y no pueden leerlo.
`,
  },
];

export const legalDoc = (slug: string) => LEGAL_DOCS.find((d) => d.slug === slug) ?? null;
/** Documentos que la empresa acepta al contratar, con su versión vigente. */
export const ACCEPTANCE_DOCS = LEGAL_DOCS.filter((d) => d.requiresAcceptance).map((d) => ({ slug: d.slug, title: d.title, version: d.version }));
